import {
  getMarioDeepSearchApi,
  getMarioSearchTypesApi,
  postMarioDeepSearchApi,
  postMarioSearchTypesApi,
  getMarioSearchConfigApi,
} from '@/services/walle';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const SearchConfigContext = createContext<SearchConfigContextType | undefined>(
  undefined,
);

interface SearchConfigContextType {
  deepSearchOpened: boolean;
  onlineSearchOpened: boolean;
  enterpriseSearchOpened: boolean;
  toggleDeepSearch: () => void;
  toggleOnlineSearch: () => void;
  toggleEnterpriseSearch: () => void;
  setDeepSearchOpened: (opened: boolean) => void;
  getSearchConfig: (conversationId: string) => void;
  resetSearchConfig: () => void;
  skipFetchRef: React.MutableRefObject<boolean>;
}
export function SearchConfigContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [deepSearchOpened, setDeepSearchOpened] = useState(false);
  const [onlineSearchOpened, setOnlineSearchOpened] = useState(false);
  const [enterpriseSearchOpened, setEnterpriseSearchOpened] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');

  const toggleOnlineSearch = useCallback(() => {
    if (deepSearchOpened) return;
    setOnlineSearchOpened(v => !v);
  }, [deepSearchOpened]);
  const toggleEnterpriseSearch = useCallback(() => {
    setEnterpriseSearchOpened(v => !v);
  }, []);
  const toggleDeepSearch = useCallback(() => {
    setDeepSearchOpened(!deepSearchOpened);
    if (!onlineSearchOpened && !deepSearchOpened) {
      toggleOnlineSearch();
    }
  }, [
    deepSearchOpened,
    onlineSearchOpened,
    toggleOnlineSearch,
  ]);
  const _setDeepSearchOpened = useCallback((opened: boolean) => {
    setDeepSearchOpened(opened);
    if (opened) {
      setOnlineSearchOpened(true);
    }
  }, []);
  const getSearchConfig = useCallback((conversationId: string) => {
    getMarioSearchConfigApi({ conversation_id: conversationId }).then((res) => {
      if (res.data) {
        if (res.data.includes('enterprise')) {
          setEnterpriseSearchOpened(true);
        } else {
          setEnterpriseSearchOpened(false);
        }
        if (res.data.includes('internet')) {
          setOnlineSearchOpened(true);
        } else {
          setOnlineSearchOpened(false);
        }
        if (res.data.includes('deep_search')) {
          _setDeepSearchOpened(true);
        } else {
          _setDeepSearchOpened(false);
        }
      }
    });
  }, []);
  useEffect(() => {
    resetSearchConfig();
  }, []);

  const resetSearchConfig = useCallback(() => {
    setDeepSearchOpened(false);
    setOnlineSearchOpened(true);
    setEnterpriseSearchOpened(false);
  }, []);

  const skipFetchRef = useRef(false);

  const value = {
    deepSearchOpened,
    onlineSearchOpened,
    enterpriseSearchOpened,
    toggleDeepSearch,
    toggleOnlineSearch,
    toggleEnterpriseSearch,
    setConversationId,
    resetSearchConfig,
    getSearchConfig,
    setDeepSearchOpened: _setDeepSearchOpened,
    skipFetchRef,
  };

  return (
    <SearchConfigContext.Provider value={value}>
      {children}
    </SearchConfigContext.Provider>
  );
}

export const useSearchConfigContext = () => {
  const context = useContext(SearchConfigContext);
  if (context === undefined) {
    throw new Error(
      'useSearchConfigContext must be used within a SearchConfigContextProvider',
    );
  }
  return context;
};
